'use client'

import React, { ReactElement, ReactNode, useState } from 'react'
import { usePopper } from 'react-popper'

import { clsx } from 'clsx'

interface Props {
  adjustToParent?: boolean
  children: ReactElement<any>
  text: ReactNode
}

const Tooltip: React.FC<Props> = ({ adjustToParent = false, children, text }) => {
  const [show, setShow] = useState(false)
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null)

  const { attributes, styles } = usePopper(referenceElement, popperElement, {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
      {
        name: 'arrow',
        options: {
          element: arrowElement,
          padding: 5,
        },
      },
    ],
  })

  return (
    <>
      <div
        className="ml-semantic-global-xs focus-visible:outline-semantic-global-primary-default flex cursor-pointer items-center focus-visible:outline-2 focus-visible:outline-offset-2"
        onBlur={() => setShow(false)}
        onFocus={() => setShow(true)}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        ref={setReferenceElement}
        tabIndex={0}
      >
        {children}
      </div>
      {show ? (
        <div
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
          className={clsx(
            'bg-semantic-global-neutral-dark text-semantic-global-text-lightest typography-body-sm-regular z-index-tooltip px-3 py-2 text-left text-sm shadow-lg',
            adjustToParent ? '' : 'mx-semantic-global-md'
          )}
        >
          {text}
          <div
            className="bg-semantic-global-neutral-dark absolute h-3 w-3"
            data-popper-arrow
            ref={setArrowElement}
            style={{
              ...styles.arrow,
              transform: `${styles.arrow?.transform || ''} rotate(45deg)`,
              ...(attributes.popper?.['data-popper-placement']?.startsWith('top') && {
                bottom: '-5px',
                marginLeft: `${adjustToParent ? '0' : '-16px'}`,
              }),
              ...(attributes.popper?.['data-popper-placement']?.startsWith('bottom') && {
                marginLeft: `${adjustToParent ? '0' : '-16px'}`,
                top: '-5px',
              }),
            }}
          />
        </div>
      ) : null}
    </>
  )
}

export default Tooltip
